<script lang="typescript">
  import { fade } from "svelte/transition";
  import { cubicOut } from "svelte/easing";
  import { createEventDispatcher, onMount } from "svelte";
  import { DateTime } from "luxon";
  import { attendantName, type OrderData } from "./stores";

  export let order: OrderData;
  let localTime = DateTime.local();
  let showTakeoverDialog = false;
  const dispatch = createEventDispatcher();

  // context menu
  let showContextMenu = false;
  let contextMenuX: number;
  let contextMenuY: number;

  const handleContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    if (!order.selected) return;

    contextMenuX = e.clientX;
    contextMenuY = e.clientY;
    showContextMenu = true;
  };

  const closeContextMenu = () => {
    if (longPressType === "touch") {
      longPressType = null;
      return;
    }
    showContextMenu = false;
    longPressType = null;
  };

  const handleUnselect = () => {
    dispatch("unselected");
    closeContextMenu();
  };

  // long press
  let longPressTimer: number = Number.MIN_VALUE;
  let longPressType: null | "touch" | "mouse" = null;

  const handlePressStart = (e: TouchEvent | MouseEvent) => {
    longPressType = null;

    let x: number;
    let y: number;
    let type: "touch" | "mouse";

    if ("touches" in e) {
      type = "touch";
      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
    } else if (e.button === 0) {
      // It's a MouseEvent that is a right click
      type = "mouse";
      x = e.clientX;
      y = e.clientY;
    } else {
      // It's a MouseEvent that is not a right click
      return;
    }

    longPressTimer = window.setTimeout(() => {
      longPressType = type;
      if (order.selected) {
        contextMenuX = x;
        contextMenuY = y;
        showContextMenu = true;
      }
    }, 500);
  };

  const handlePressEnd = () => {
    clearTimeout(longPressTimer);
  };

  const handlePressMove = () => {
    clearTimeout(longPressTimer);
  };

  // Check if order is assigned to another waiter
  $: isAssignedToOther =
    order.attendant &&
    order.attendant.trim() !== "" &&
    order.attendant !== $attendantName;

  onMount(() => {
    const interval = setInterval(() => {
      localTime = DateTime.local();
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  });

  const onOrderClick = () => {
    if (longPressType || showContextMenu) return;
    if (isAssignedToOther) {
      showTakeoverDialog = true;
    } else {
      dispatch(`${!order.selected ? "selected" : "fulfilled"}`);
    }
  };

  const handleTakeoverConfirm = () => {
    showTakeoverDialog = false;
    dispatch("takeover");
  };

  const handleTakeoverCancel = () => {
    showTakeoverDialog = false;
  };

  const timeSinceOrderCreation = (localTime: DateTime, created: string) =>
    localTime
      .diff(DateTime.fromISO(created, { zone: "UTC" }), ["minutes", "seconds"])
      .toFormat("mm:ss");
</script>

<svelte:window on:mousedown={closeContextMenu} />

{#if showContextMenu}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="context-menu"
    style="top: {contextMenuY}px; left: {contextMenuX}px;"
    on:mousedown|stopPropagation
  >
    <ul>
      <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->

      <li on:mousedown={handleUnselect}>Un-select</li>
    </ul>
  </div>
{/if}

{#if !order.done}
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <li
    transition:fade|global={{ duration: 1000, easing: cubicOut }}
    class="order"
    class:selected={order.selected}
    class:assignedToOther={isAssignedToOther}
    data-order-id={order.id}
    style="order: {order.id}"
    on:click={onOrderClick}
    on:touchstart={handlePressStart}
    on:touchend={handlePressEnd}
    on:touchmove={handlePressMove}
    on:mousedown={handlePressStart}
    on:mouseup={handlePressEnd}
    on:mousemove={handlePressMove}
    on:contextmenu={handleContextMenu}
  >
    <h2>{order.table}</h2>
    <div class="order-age">
      ⏲️ {timeSinceOrderCreation(localTime, order.created)}
    </div>
    <div class="attendant-display">{order.attendant ?? ""}</div>
    <ul>
      {#each order.items as item}
        <li>
          {item.amount}x {item.drink}
        </li>
      {/each}
    </ul>
  </li>
{/if}

<!-- Takeover confirmation dialog -->
{#if showTakeoverDialog}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="dialog-overlay" on:click={handleTakeoverCancel}>
    <div class="dialog" on:mousedown|stopPropagation>
      <h3>Take over order?</h3>
      <p>
        This order is currently assigned to <strong>{order.attendant}</strong>.
      </p>
      <p>Do you want to take over this order?</p>
      <div class="dialog-buttons">
        <button class="btn-cancel" on:click={handleTakeoverCancel}
          >Cancel</button
        >
        <button class="btn-confirm" on:click={handleTakeoverConfirm}
          >Take Over</button
        >
      </div>
    </div>
  </div>
{/if}

<style>
  li.order {
    counter-increment: my-awesome-counter;
    position: relative;
    min-height: 200px;
    float: left;
    margin: 20px 20px;
    width: 300px;
    padding: 25px 15px;
    background: #fefabc linear-gradient(150deg, #efec88 0%, #fefabc 100%);
    border: 1px solid #cccccc;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    cursor: pointer;
  }

  li.assignedToOther {
    transform: scale(0.85);
    opacity: 0.6;
    background: #e8e8e8 linear-gradient(150deg, #d0d0d0 0%, #e8e8e8 100%);
    filter: grayscale(0.7);
  }

  li.assignedToOther:hover {
    opacity: 0.8;
    filter: grayscale(0.5);
  }

  li.selected {
    transform: scale(1.08) rotate(1.5deg);
    background: #bcfebc linear-gradient(150deg, #88efbb 0%, #bcfec7 100%);
  }

  /* Override the assignedToOther styles when selected */
  li.selected.assignedToOther {
    opacity: 1;
    filter: none;
  }
  li.order::before {
    content: counter(my-awesome-counter);
    color: #ffffff;
    font-size: 1.5rem;
    font-weight: bold;
    position: absolute;
    --size: 32px;
    left: calc(-1 * var(--size) + 15px);
    line-height: calc(var(--size) + 4px);
    width: var(--size);
    height: var(--size);
    top: -15px;
    transform: rotate(-10deg);
    background: #ff0000
      radial-gradient(at 8px 8px, rgba(255, 255, 255, 0), rgba(0, 0, 0, 0.5));
    border-radius: 50%;
    text-align: center;
    box-shadow: 4px 4px 3px rgba(0, 0, 0, 0.5);
  }
  h2 {
    text-align: center;
    line-height: 1em;
    font-weight: bold;
    font-size: 1.4em;
    margin: 0.25em 0;
    font-family: Header, cursive;
  }

  .order-age {
    position: absolute;
    bottom: 5px;
    left: 10px;
  }

  .order-age {
    position: absolute;
    bottom: 5px;
    left: 10px;
  }

  .attendant-display {
    position: absolute;
    bottom: 5px;
    right: 10px;
  }

  /* Dialog styles */
  .dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .dialog {
    background: white;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    max-width: 400px;
    text-align: center;
  }

  .dialog h3 {
    margin: 0 0 15px 0;
    color: #333;
  }

  .dialog p {
    margin: 10px 0;
    color: #666;
  }

  .dialog-buttons {
    display: flex;
    gap: 15px;
    justify-content: center;
    margin-top: 20px;
  }

  .dialog-buttons button {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
  }

  .btn-cancel {
    background: #e0e0e0;
    color: #333;
  }

  .btn-cancel:hover {
    background: #d0d0d0;
  }

  .btn-confirm {
    background: #ff6b6b;
    color: white;
  }

  .btn-confirm:hover {
    background: #e55555;
  }
  .context-menu {
    position: fixed;
    background: white;
    border: 1px solid #ccc;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    border-radius: 4px;
    overflow: hidden;
  }

  .context-menu ul {
    list-style: none;
    margin: 0;
    padding: 5px 0;
  }

  .context-menu li {
    padding: 8px 15px;
    cursor: pointer;
    min-height: auto;
    float: none;
    margin: 0;
    width: auto;
    background: none;
    border: none;
    box-shadow: none;
  }

  .context-menu li:hover {
    background: #f5f5f5;
  }
</style>
